import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaComments, FaList, FaDatabase, FaBolt, FaPlus, FaEdit, FaServer, FaUserCog, FaBrain, FaProjectDiagram, FaUser, FaSignOutAlt, FaCog } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import brainLogo from '../assets/IMG_0118.png';
import './Navigation.css';

const Navigation = () => {
  const { currentUser, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  
  const toggleDropdown = (dropdown) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Toggle the dropdown
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };
  
  const closeDropdown = () => {
    setActiveDropdown(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleDropdownItemClick = () => {
    // Close dropdown immediately when any item is clicked
    closeDropdown();
    setIsOpen(false);
  };

  const handleLogout = async () => {
    try { 
      await logout(); 
      navigate('/login'); 
    } catch (error) { 
      console.error('Failed to log out', error); 
    }
    handleDropdownItemClick();
  };

  // Handle outside clicks and auto-close timer
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Start 5-second timer when clicking outside
        if (activeDropdown && !timeoutRef.current) {
          timeoutRef.current = setTimeout(() => {
            setActiveDropdown(null);
            timeoutRef.current = null;
          }, 5000);
        }
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [activeDropdown]);

  const isActive = (path) => location.pathname === path;
  const isActivePrefix = (prefix) => location.pathname.startsWith(prefix);

  if (['/login', '/signup', '/reset-password'].includes(location.pathname)) return null;

  return (
    <nav className="navigation">
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          <img src={brainLogo} alt="Brain Logo" />
          Agent Developer
        </Link>

        <div className="menu-toggle" onClick={toggleMenu}>
          <div className={`hamburger ${isOpen ? 'active' : ''}`}><span></span><span></span><span></span></div>
        </div>

        <ul className={`nav-menu ${isOpen ? 'active' : ''}`}>
          <li><Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`} onClick={() => setIsOpen(false)}>Home</Link></li>
          
          <li className="dropdown">
            <div className={`dropdown-toggle ${isActivePrefix('/chat') || isActivePrefix('/knowledge-base') ? 'active' : ''}`} onClick={() => toggleDropdown('neural')}>
              <FaComments /> Neural Interface <span className="arrow-down"></span>
            </div>
            <div className={`dropdown-menu ${activeDropdown === 'neural' ? 'show' : ''}`}>
              <Link to="/chat" className={`dropdown-item ${isActive('/chat') ? 'active' : ''}`} onClick={handleDropdownItemClick}><FaComments /> Agent Chat</Link>
              <Link to="/knowledge-base" className={`dropdown-item ${isActive('/knowledge-base') ? 'active' : ''}`} onClick={handleDropdownItemClick}><FaDatabase /> Knowledge Matrix</Link>
            </div>
          </li>
          
          <li className="dropdown">
            <div className={`dropdown-toggle ${isActivePrefix('/agents') || isActivePrefix('/ai-models') ? 'active' : ''}`} onClick={() => toggleDropdown('systems')}>
              <FaBolt /> Agent Systems <span className="arrow-down"></span>
            </div>
            <div className={`dropdown-menu ${activeDropdown === 'systems' ? 'show' : ''}`}>
              <Link to="/agents/list" className="dropdown-item" onClick={handleDropdownItemClick}><FaList /> Agent Registry</Link>
              <Link to="/agents/add" className="dropdown-item" onClick={handleDropdownItemClick}><FaPlus /> Create Agent</Link>
              <Link to="/agents/develop" className="dropdown-item" onClick={handleDropdownItemClick}><FaEdit /> Development Lab</Link>
              <Link to="/ai-models" className="dropdown-item" onClick={handleDropdownItemClick}><FaBrain /> AI Models</Link>
              <Link to="/agent-structure" className="dropdown-item" onClick={handleDropdownItemClick}><FaProjectDiagram /> Structure</Link>
              <Link to="/agents/deploy" className="dropdown-item" onClick={handleDropdownItemClick}><FaServer /> Deployment</Link>
            </div>
          </li>

          <li><Link to="/user-profile" className={`nav-link ${isActive('/user-profile') ? 'active' : ''}`} onClick={() => setIsOpen(false)}>Account</Link></li>
          {isAdmin && <li><Link to="/user-admin" className={`nav-link ${isActive('/user-admin') ? 'active' : ''}`} onClick={() => setIsOpen(false)}><FaUserCog /> Admin</Link></li>}
        </ul>

        <div className="nav-right">
          {currentUser && (
            <div className="user-dropdown dropdown" ref={dropdownRef}>
              <div className="dropdown-toggle user-toggle" onClick={() => toggleDropdown('user')}>
                <FaUser /> <span className="user-email d-none d-md-inline">{currentUser.email}</span> <span className="arrow-down"></span>
              </div>
              <div className={`dropdown-menu user-dropdown-menu ${activeDropdown === 'user' ? 'show' : ''}`}>
                <Link to="/user-profile" className="dropdown-item" onClick={handleDropdownItemClick}><FaUser /> Profile</Link>
                <button className="dropdown-item sign-out-btn" onClick={handleLogout}><FaSignOutAlt /> Logout</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;